import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// فقط برای refresh از این instance استفاده می‌کنیم تا loop ایجاد نشود
const refreshApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];
let interceptorsInitialized = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const clearAuthStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

const getAccessToken = () => localStorage.getItem("accessToken");
const getRefreshToken = () => localStorage.getItem("refreshToken");

// افزودن توکن در همه درخواست‌ها در صورت وجود
api.interceptors.request.use(
  (config) => {
    const access = getAccessToken();

    if (access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${access}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export const setupInterceptors = (logout) => {
  // جلوگیری از ثبت چندباره interceptor
  if (interceptorsInitialized) return;

  interceptorsInitialized = true;

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      // قطع اینترنت یا در دسترس نبودن سرور
      if (!error.response) {
        return Promise.reject({
          code: "NETWORK_ERROR",
          message: "رتباط با سرور برقرار نشد.",
          originalError: error,
        });
      }

      const originalRequest = error.config;
      const status = error.response.status;

      if (status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      // اگر خود درخواست refresh شکست خورده، دیگر تلاش مجدد نکن
      if (originalRequest.url?.includes("/api/token/refresh/")) {
        clearAuthStorage();
        if (typeof logout === "function") {
          logout();
        } else {
          window.location.href = "/user-login/";
        }
        return Promise.reject(error);
      }

      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      // اگر اصلاً کاربر لاگین نیست، فقط خطا را برگردان
      // این بخش مهم است تا صفحات عمومی با 401 داخلی به login پرت نشوند
      if (!accessToken) {
        return Promise.reject(error);
      }

      // اگر قبلاً یک بار retry شده و باز 401 گرفته، session معتبر نیست
      if (originalRequest._retry) {
        clearAuthStorage();
        if (typeof logout === "function") {
          logout();
        } else {
          window.location.href = "/user-login/";
        }
        return Promise.reject(error);
      }

      // اگر refresh token نداریم، logout
      if (!refreshToken) {
        clearAuthStorage();
        if (typeof logout === "function") {
          logout();
        } else {
          window.location.href = "/user-login/";
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // اگر refresh در حال انجام است، درخواست را در صف نگه دار
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newAccessToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          })
          .catch((queueError) => Promise.reject(queueError));
      }

      isRefreshing = true;

      try {
        const response = await refreshApi.post("/token/refresh/", {
          refresh: refreshToken,
        });

        const newAccessToken = response.data?.access;

        if (!newAccessToken) {
          throw new Error("No access token returned from refresh endpoint");
        }

        localStorage.setItem("accessToken", newAccessToken);

        processQueue(null, newAccessToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthStorage();

        if (typeof logout === "function") {
          logout();
        } else {
          window.location.href = "/user-login/";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export default api;
